import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UpcomingActivity, ActivityType } from '../../../../models/profile.interface';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { IconType } from '../../../../../shared/services/icon.service';

@Component({
  selector: 'app-upcoming-bookings',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './upcoming-bookings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './upcoming-bookings.component.css',
})
export class UpcomingBookingsComponent {
  @Input() activities: UpcomingActivity[] = [];
  @Input() loading = false;
  @Output() viewAll = new EventEmitter<void>();
  @Output() bookNow = new EventEmitter<void>();

  readonly IconType = IconType;

  /**
   * Format date for display with relative labels (Today, Tomorrow).
   *
   * Uses BROWSER timezone intentionally - this is a user-centric view
   * answering "when is MY booking?" per docs/TIMEZONE_HANDLING.md guidelines.
   * Users should see bookings in their personal calendar context.
   */
  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const isToday = date.toDateString() === today.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();

    if (isToday) {
      return 'Heute';
    }
    if (isTomorrow) {
      return 'Morgen';
    }

    return date.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    });
  }

  /**
   * Format time for display.
   *
   * Uses BROWSER timezone intentionally for user-centric views.
   * See docs/TIMEZONE_HANDLING.md for venue vs browser timezone rationale.
   */
  formatTime(dateStr: string): string {
    return new Date(dateStr).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatDuration(start: string, end: string): string {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate.getTime() - startDate.getTime();
    const diffMins = Math.round(diffMs / 60000);

    if (diffMins >= 60) {
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    }
    return `${diffMins}min`;
  }

  getActivityTypeLabel(type: ActivityType): string {
    switch (type) {
      case 'course':
        return 'Kurs';
      case 'subscription':
        return 'Abo';
      default:
        return 'Buchung';
    }
  }

  getActivityTypeIcon(type: ActivityType): IconType {
    switch (type) {
      case 'course':
        return IconType.USERS;
      case 'subscription':
        return IconType.REPEAT;
      default:
        return IconType.CALENDAR;
    }
  }

  getActivityTypeClass(type: ActivityType): string {
    return `type-badge type-${type}`;
  }

  onViewAll(): void {
    this.viewAll.emit();
  }

  onBookNow(): void {
    this.bookNow.emit();
  }
}
